import {
  Box,
  CircularProgress,
  FormControl,
  FormErrorMessage,
  FormLabel,
  GridItem,
  HStack,
  Input,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  isValidSpanishTaxId,
  normalizeTaxId,
} from "../../helpers/spanish-tax-id";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/chackra-ui-button";
import { ProvinceSelect } from "../../components/province-select";
import { spanishProvinces } from "../../helpers/spanish-provinces";
import {
  useTaxProfileQuery,
  useUpsertTaxProfileMutation,
} from "../../graphql/generated/user-v2-hooks";
import { Routes } from "../../helpers/router";
import { TFunction } from "i18next";
import * as Yup from "yup";

type TaxProfileFormValues = {
  nif: string;
  name: string;
  address: string;
  city: string;
  zipCode: string;
  province: string;
};

const validationSchema = (t: TFunction) =>
  Yup.object({
    nif: Yup.string()
      .required(t("taxProfile.fieldRequired"))
      .trim()
      .transform((value) =>
        typeof value === "string" ? normalizeTaxId(value) : value,
      )
      .test("nif-valid", t("taxProfile.nifInvalid"), (value) => {
        if (!value) return false;
        return isValidSpanishTaxId(value);
      }),
    name: Yup.string().required(t("taxProfile.fieldRequired")).trim(),
    address: Yup.string().required(t("taxProfile.fieldRequired")).trim(),
    city: Yup.string().required(t("taxProfile.fieldRequired")).trim(),
    zipCode: Yup.string().required(t("taxProfile.fieldRequired")).trim(),
    province: Yup.string()
      .required(t("taxProfile.fieldRequired"))
      .trim()
      .test("valid-province", t("taxProfile.provinceInvalid"), (value) =>
        value ? spanishProvinces.some((p) => p.code === value) : false,
      ),
  });

export const TaxProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const { data, loading, error, refetch } = useTaxProfileQuery({
    context: { endpoint: "fitnextv2" },
    fetchPolicy: "network-only",
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<TaxProfileFormValues>({
    resolver: yupResolver(validationSchema(t)),
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (data?.taxProfile) {
      reset({
        nif: data.taxProfile.nif,
        name: data.taxProfile.name,
        address: data.taxProfile.address,
        city: data.taxProfile.city,
        zipCode: data.taxProfile.zipCode,
        province: data.taxProfile.province,
      });
    }
  }, [data, reset]);

  const [upsertTaxProfile] = useUpsertTaxProfileMutation({
    context: { endpoint: "fitnextv2" },
    onCompleted: () => {
      toast({
        title: t("taxProfile.saved"),
        status: "success",
        isClosable: true,
      });
      navigate(Routes.Dashboard());
    },
    onError: () => {
      toast({
        title: t("taxProfile.error"),
        status: "error",
        isClosable: true,
      });
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    await upsertTaxProfile({
      variables: {
        input: {
          nif: normalizeTaxId(values.nif),
          name: values.name,
          address: values.address,
          city: values.city,
          zipCode: values.zipCode,
          province: values.province,
          country: "ES",
        },
      },
    });
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
        <CircularProgress isIndeterminate color="blue.500" size={6} />
      </Box>
    );
  }

  if (error) {
    return (
      <VStack flex={1} justify="center" spacing={4}>
        <Text fontSize="14px" color="red.500">
          {t("taxProfile.loadError")}
        </Text>
        <Button variant="senseiOutlined" onClick={() => refetch()}>
          {t("common.retry")}
        </Button>
      </VStack>
    );
  }

  return (
    <VStack w="full" flex={1} justify="center" spacing={0}>
      <VStack w="full" maxW="580px" spacing={0} mx="auto" px={4}>
        <Text
          fontFamily="'Barlow Condensed', sans-serif"
          fontSize="28px"
          fontWeight={700}
          color="#13192E"
          textAlign="center"
          mb={7}
        >
          {t("taxProfile.title")}
        </Text>
        <form onSubmit={onSubmit} noValidate autoComplete="off" style={{ width: "100%" }}>
          <VStack spacing={4} w="full">
            <Box w="full" bg="slate.50" borderRadius="10px" p={4} mb={1}>
              <Text fontSize="14px" color="slate.500" lineHeight="1.5">
                {t("taxProfile.notice")}
              </Text>
            </Box>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
              <FormControl isInvalid={!!errors.nif} isRequired>
                <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                  {t("taxProfile.nif")}
                </FormLabel>
                <Input

                  {...register("nif")}
                />
                {errors.nif?.message && (
                  <FormErrorMessage>{errors.nif.message}</FormErrorMessage>
                )}
              </FormControl>
              <FormControl isInvalid={!!errors.name} isRequired>
                <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                  {t("taxProfile.name")}
                </FormLabel>
                <Input

                  {...register("name")}
                />
                {errors.name?.message && (
                  <FormErrorMessage>{errors.name.message}</FormErrorMessage>
                )}
              </FormControl>
            </SimpleGrid>
            <FormControl isInvalid={!!errors.address} isRequired>
              <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                {t("taxProfile.address")}
              </FormLabel>
              <Input
                {...register("address")}
              />
              {errors.address?.message && (
                <FormErrorMessage>{errors.address.message}</FormErrorMessage>
              )}
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
              <FormControl isInvalid={!!errors.city} isRequired>
                <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                  {t("taxProfile.city")}
                </FormLabel>
                <Input

                  {...register("city")}
                />
                {errors.city?.message && (
                  <FormErrorMessage>{errors.city.message}</FormErrorMessage>
                )}
              </FormControl>
              <FormControl isInvalid={!!errors.zipCode} isRequired>
                <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                  {t("taxProfile.zipCode")}
                </FormLabel>
                <Input

                  {...register("zipCode")}
                />
                {errors.zipCode?.message && (
                  <FormErrorMessage>{errors.zipCode.message}</FormErrorMessage>
                )}
              </FormControl>
              <FormControl isInvalid={!!errors.province} isRequired>
                <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                  {t("taxProfile.province")}
                </FormLabel>
                <Controller
                  name="province"
                  control={control}
                  render={({ field }) => (
                    <ProvinceSelect
                      id="province"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.province?.message && (
                  <FormErrorMessage>{errors.province.message}</FormErrorMessage>
                )}
              </FormControl>
            </SimpleGrid>
            <Button
              isLoading={isSubmitting}
              type="submit"
              variant="senseiFilled"
              w="full"
              mt={2}
            >
              {t("taxProfile.save")}
            </Button>
          </VStack>
        </form>
      </VStack>
    </VStack>
  );
};
